// =============================================================================
// shareUtils.js - Share Link Management Utility
// =============================================================================
// Handles creation and management of shareable allocation links
// Uses lightweight metadata documents with real-time data loading
// Supports multi-facility sharing
// =============================================================================

import { doc, setDoc } from 'firebase/firestore';
import { db, auth } from './firebase';

/**
 * Generate a unique share ID
 * @returns {string} Unique share ID (e.g., "1731234567890-abc123xyz")
 */
const generateShareId = () => {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Create a shareable link for pitch allocations
 * 
 * @param {Object} params - Share parameters
 * @param {string} params.clubId - Club identifier
 * @param {string} params.clubName - Club name (for analytics)
 * @param {string} params.pitchId - Pitch identifier (e.g., "pitch1")
 * @param {string} params.facilityId - Facility identifier (optional, for multi-facility clubs)
 * @param {string} params.facilityName - Facility name (optional, for display)
 * @param {string} params.date - Date in YYYY-MM-DD format
 * @param {number} params.start - Start hour (default: 6)
 * @param {number} params.end - End hour (default: 22)
 * @param {number} params.expiryDays - Days until link expires (default: 30)
 * 
 * @returns {Promise<Object>} Share result with success status and link
 * 
 * @example
 * const result = await createShareLink({
 *   clubId: "1RFZCF",
 *   clubName: "Adelaide United FC",
 *   pitchId: "pitch1",
 *   facilityId: "facility-abc123",
 *   facilityName: "Main Ground",
 *   date: "2025-11-11",
 *   start: 8,
 *   end: 21
 * });
 * 
 * if (result.success) {
 *   console.log('Share link:', result.link);
 * }
 */
export const createShareLink = async ({
  clubId,
  clubName = 'Unknown Club',
  pitchId,
  facilityId = null,
  facilityName = null,
  date,
  start = 6,
  end = 22,
  expiryDays = 30
}) => {
  try {
    // Validation
    if (!clubId || !pitchId || !date) {
      throw new Error('Missing required parameters: clubId, pitchId, or date');
    }
    
    // Validate date format
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(date)) {
      throw new Error('Invalid date format. Use YYYY-MM-DD');
    }
    
    // Generate unique share ID
    const shareId = generateShareId();
    
    // Calculate expiration date
    const expiresAt = new Date(Date.now() + expiryDays * 24 * 60 * 60 * 1000);
    
    // Get current user info
    const currentUser = auth.currentUser;
    const createdBy = currentUser?.email || 'unknown';
    const creatorId = currentUser?.uid || null;
    
    // Create share metadata document
    const shareData = {
      // Core identifiers (what to load)
      clubId,
      clubName,
      pitchId,
      date,
      
      // Multi-facility support
      facilityId: facilityId || null,
      facilityName: facilityName || null,
      
      // Time range
      start,
      end,
      
      // Analytics
      views: 0,
      lastViewed: null,
      
      // Metadata
      createdAt: new Date().toISOString(),
      createdBy,
      creatorId,
      expiresAt: expiresAt.toISOString(),
      
      // Version for future compatibility
      version: 2  // Incremented for facility support
    };
    
    // Save to Firestore
    await setDoc(doc(db, 'sharedAllocations', shareId), shareData);
    
    // Generate share URL - always use production domain
    const shareLink = `https://pitchallo.app/share/${shareId}`;
    
    console.log('✅ Share link created successfully');
    console.log('📊 Share ID:', shareId);
    console.log('🔗 Link:', shareLink);
    console.log('🏟️ Facility:', facilityName || 'All facilities');
    console.log('📅 Expires:', expiresAt.toLocaleDateString());
    
    return {
      success: true,
      shareId,
      link: shareLink,
      expiresAt: expiresAt.toISOString(),
      metadata: shareData
    };
    
  } catch (error) {
    console.error('❌ Error creating share link:', error);
    
    return {
      success: false,
      error: error.message || 'Failed to create share link',
      link: null
    };
  }
};

/**
 * Create a share link for multiple pitches/dates
 * Useful for sharing an entire day or week of allocations
 * 
 * @param {Object} params - Share parameters
 * @param {string} params.clubId - Club identifier
 * @param {string} params.clubName - Club name
 * @param {string} params.facilityId - Facility identifier (optional)
 * @param {string} params.facilityName - Facility name (optional)
 * @param {Array<string>} params.dates - Array of dates (YYYY-MM-DD)
 * @param {Array<string>} params.pitchIds - Array of pitch IDs
 * @param {number} params.start - Start hour
 * @param {number} params.end - End hour
 * @param {number} params.expiryDays - Days until expiration
 * 
 * @returns {Promise<Object>} Share result with success status and link
 */
export const createMultiShareLink = async ({
  clubId,
  clubName = 'Unknown Club',
  facilityId = null,
  facilityName = null,
  dates = [],
  pitchIds = [],
  start = 6,
  end = 22,
  expiryDays = 30
}) => {
  try {
    if (!clubId || dates.length === 0) {
      throw new Error('Missing required parameters: clubId or dates');
    }
    
    const shareId = generateShareId();
    const expiresAt = new Date(Date.now() + expiryDays * 24 * 60 * 60 * 1000);
    const currentUser = auth.currentUser;
    
    const shareData = {
      clubId,
      clubName,
      facilityId: facilityId || null,
      facilityName: facilityName || null,
      dates,
      pitchIds: pitchIds.length > 0 ? pitchIds : null, // null = all pitches
      start,
      end,
      views: 0,
      lastViewed: null,
      createdAt: new Date().toISOString(),
      createdBy: currentUser?.email || 'unknown',
      creatorId: currentUser?.uid || null,
      expiresAt: expiresAt.toISOString(),
      type: 'multi', // Mark as multi-date share
      version: 2
    };
    
    await setDoc(doc(db, 'sharedAllocations', shareId), shareData);
    
    // Generate share URL - always use production domain
    const shareLink = `https://pitchallo.app/share/${shareId}`;
    
    console.log('✅ Multi-date share link created');
    console.log('📊 Share ID:', shareId);
    console.log('🏟️ Facility:', facilityName || 'All facilities');
    console.log('📅 Dates:', dates.join(', '));
    
    return {
      success: true,
      shareId,
      link: shareLink,
      expiresAt: expiresAt.toISOString(),
      metadata: shareData
    };
    
  } catch (error) {
    console.error('❌ Error creating multi-share link:', error);
    
    return {
      success: false,
      error: error.message || 'Failed to create share link',
      link: null
    };
  }
};

/**
 * Validate share link parameters before creation
 * 
 * @param {Object} params - Parameters to validate
 * @returns {Object} Validation result with isValid boolean and error message
 */
export const validateShareParams = (params) => {
  const { clubId, pitchId, date } = params;
  
  if (!clubId) {
    return { isValid: false, error: 'Club ID is required' };
  }
  
  if (!pitchId) {
    return { isValid: false, error: 'Pitch ID is required' };
  }
  
  if (!date) {
    return { isValid: false, error: 'Date is required' };
  }
  
  // Validate date format
  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (!dateRegex.test(date)) {
    return { isValid: false, error: 'Invalid date format. Use YYYY-MM-DD' };
  }
  
  // Validate date is not in the past (optional check)
  const shareDate = new Date(date);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  if (shareDate < today) {
    // Warning, but not blocking
    console.warn('⚠️ Sharing a past date');
  }
  
  return { isValid: true, error: null };
};

/**
 * Format share link for display
 * Shortens the link for better readability
 * 
 * @param {string} link - Full share link
 * @returns {string} Shortened display version
 */
export const formatShareLinkForDisplay = (link) => {
  try {
    const url = new URL(link);
    const shareId = url.pathname.split('/').pop();
    
    // Show only last 8 characters of share ID
    const shortId = shareId.slice(-8);
    
    return `${url.origin}/share/...${shortId}`;
  } catch (error) {
    return link;
  }
};

/**
 * Copy share link to clipboard
 * 
 * @param {string} link - Share link to copy
 * @returns {Promise<boolean>} Success status
 */
export const copyShareLinkToClipboard = async (link) => {
  try {
    await navigator.clipboard.writeText(link);
    console.log('✅ Link copied to clipboard');
    return true;
  } catch (error) {
    console.error('❌ Failed to copy link:', error);
    
    // Fallback for older browsers
    try {
      const textArea = document.createElement('textarea');
      textArea.value = link;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      console.log('✅ Link copied using fallback method');
      return true;
    } catch (fallbackError) {
      console.error('❌ Fallback copy failed:', fallbackError);
      return false;
    }
  }
};

/**
 * Get share statistics (for future analytics dashboard)
 * 
 * @param {string} shareId - Share ID to get stats for
 * @returns {Promise<Object>} Share statistics
 */
export const getShareStats = async (shareId) => {
  try {
    const { doc, getDoc } = await import('firebase/firestore');
    
    const shareDoc = await getDoc(doc(db, 'sharedAllocations', shareId));
    
    if (!shareDoc.exists()) {
      throw new Error('Share not found');
    }
    
    const data = shareDoc.data();
    
    return {
      success: true,
      stats: {
        views: data.views || 0,
        createdAt: data.createdAt,
        lastViewed: data.lastViewed,
        createdBy: data.createdBy,
        expiresAt: data.expiresAt,
        isExpired: new Date(data.expiresAt) < new Date(),
        facilityId: data.facilityId,
        facilityName: data.facilityName
      }
    };
    
  } catch (error) {
    console.error('Error getting share stats:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Default export for convenience
export default {
  createShareLink,
  createMultiShareLink,
  validateShareParams,
  formatShareLinkForDisplay,
  copyShareLinkToClipboard,
  getShareStats
};
